import { useTranslation } from 'react-i18next';
import { Crown, Check, Smartphone, Sparkles, ExternalLink } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { useSubscriptionStatus } from '@/features/settings/hooks/use-subscription';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.frappi.app';

interface UpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const premiumFeatures = [
  'Estudiantes ilimitados',
  'Tareas ilimitadas',
  'Materias ilimitadas',
  'IA sin límites (escaneos y ayuda)',
  'Planes de estudio personalizados',
  'Soporte prioritario',
];

function UsageBar({ label, used, max }: { label: string; used: number; max: number | null }) {
  if (max === null) return null;
  const percentage = Math.min((used / max) * 100, 100);
  const isExceeded = used >= max;
  const isNearLimit = percentage >= 80;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className={cn(
          'font-medium',
          isExceeded ? 'text-red-500' : isNearLimit ? 'text-amber-500' : 'text-muted-foreground'
        )}>
          {used}/{max} {isExceeded && '— Límite alcanzado'}
        </span>
      </div>
      <Progress
        value={percentage}
        className={cn(
          isExceeded ? '[&>div]:bg-red-500' : isNearLimit ? '[&>div]:bg-amber-500' : ''
        )}
      />
    </div>
  );
}

export function UpgradeDialog({ open, onOpenChange }: UpgradeDialogProps) {
  const { t } = useTranslation('settings');
  const { data: status } = useSubscriptionStatus();

  const quota = status?.usageQuota;
  const limits = status?.planLimits;
  const students = status?.studentsCount ?? 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-amber-500 to-orange-500">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <DialogTitle className="text-center">
            {t('subscription.upgrade.title')}
          </DialogTitle>
          <DialogDescription className="text-center">
            {t('subscription.upgrade.description')}
          </DialogDescription>
        </DialogHeader>

        {/* Current usage with clear limits */}
        {quota && limits && (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-xs font-medium text-muted-foreground">Tu uso actual (Plan Gratis)</p>
            <UsageBar label="Estudiantes" used={students} max={limits.maxStudents} />
            <UsageBar label="Escaneos IA / mes" used={quota.aiScansUsed} max={limits.aiScansPerMonth} />
            <UsageBar label="Ayudas IA / mes" used={quota.aiHelpsUsed} max={limits.aiHelpsPerMonth} />
            <UsageBar label="Tareas" used={quota.tasksCreated} max={limits.maxTasks} />
            <UsageBar label="Materias" used={quota.subjectsCreated} max={limits.maxSubjects} />
          </div>
        )}

        {/* Premium features */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            <Sparkles className="h-3 w-3 text-amber-500" />
            Con Premium obtienes
          </p>
          {premiumFeatures.map((feature) => (
            <div key={feature} className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-amber-500 shrink-0" />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Pricing info — actual price/trial depend on country and Play Store
            eligibility, so we don't hardcode them here */}
        <div className="text-center p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200/50 dark:border-amber-800/50">
          <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
            Suscripción mensual o anual
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Verás el precio en tu moneda y las ofertas disponibles al suscribirte en Google Play. Cancela cuando quieras.
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600"
            onClick={() => window.open(PLAY_STORE_URL, '_blank')}
          >
            <Smartphone className="mr-2 h-4 w-4" />
            Descargar App y Suscribirse
          </Button>
          <p className="text-[11px] text-center text-muted-foreground flex items-center justify-center gap-1">
            Suscripción gestionada a través de Google Play
            <ExternalLink className="h-2.5 w-2.5" />
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-muted-foreground"
            onClick={() => onOpenChange(false)}
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
